import {
  UserScore,
  UserPosition,
  UserScoreList,
  ScoreContainer,
  LeaderboardTitle,
  LeaderboardScores,
  LeaderboardDescription
} from './Leaderboard.styles';

import { useLoader } from '@CS/hooks';
import { useTranslations } from '@/hooks';
import { LeaderBoardWeeks } from './weeks';
import FirstPlace from '@CS/assets/first.svg?component';
import ThirdPlace from '@CS/assets/third.svg?component';
import { useState, useCallback, useEffect } from 'react';
import SecondPlace from '@CS/assets/second.svg?component';
import { type PopupProps, Popup } from '@CS/components/Popups';

type HighScores = readonly {
  name?: string;
  score: number;
}[];

type UserHighScore = Required<HighScores[number]> & {
  position: number;
};

export type WeekScores = (week: number) => Promise<{
  userData: UserHighScore;
  weekScores: HighScores;
}>;

type LeaderboardProps = PopupProps & {
  getWeekScores: WeekScores;
  duplicateUser?: boolean;
  currentWeek?: number;
  weeks?: boolean;
};

export const Leaderboard = ({
  duplicateUser = true,
  currentWeek = 1.0,
  getWeekScores,
  weeks = true,
  ...props
}: LeaderboardProps) => {
  const [selectedWeek, setSelectedWeek] = useState(currentWeek - 1);
  const [visibleUserScore, setVisibleUserScore] = useState(false);
  const [userData, setUserData] = useState<UserHighScore>();
  const [scores, setScores] = useState<HighScores>([]);
  const [loading, setLoading] = useState(true);
  const loader = useLoader(loading);

  const [title, description] = useTranslations([
    '09LeaderboardPopupTitle',
    '09LeaderboardPopupDescription'
  ]);

  const onWeekClick = (week: number) => setSelectedWeek(week);

  const updateSelectedWeek = useCallback(async () => {
    const { userData, weekScores } = await getWeekScores(selectedWeek);

    setVisibleUserScore(
      !!userData && !duplicateUser
        ? userData?.position > weekScores.length
        : !!userData
    );

    if (!userData.name || userData.position < 0) {
      setVisibleUserScore(false);
    }

    setUserData(userData);
    setScores(weekScores);
  }, [selectedWeek]);

  useEffect(() => {
    setLoading(true);
    updateSelectedWeek().finally(() => setLoading(false));
  }, [selectedWeek]);

  return (
    <Popup customStyle={{ overflowX: 'hidden' }} {...props} hideClose>
      <LeaderboardTitle>{title}</LeaderboardTitle>
      <LeaderboardDescription>{description}</LeaderboardDescription>

      {(weeks && (
        <LeaderBoardWeeks
          current={selectedWeek}
          onClick={onWeekClick}
          weeks={currentWeek}
        />
      )) ||
        null}

      <LeaderboardScores>
        {scores.map((user, u) => (
          <ScoreContainer key={u}>
            <UserPosition>{u + 1}</UserPosition>

            <UserScoreList>
              <span>{user.name}</span>
              {u === 0 ? (
                <FirstPlace />
              ) : u === 1 ? (
                <SecondPlace />
              ) : u === 2 ? (
                <ThirdPlace />
              ) : null}

              <UserScore>{user.score}</UserScore>
            </UserScoreList>
          </ScoreContainer>
        ))}

        {userData && visibleUserScore && (
          <ScoreContainer $user={!!userData}>
            <UserPosition>{userData.position}</UserPosition>

            <UserScoreList>
              <span>{userData.name}</span>
              <UserScore>{userData.score}</UserScore>
            </UserScoreList>
          </ScoreContainer>
        )}
      </LeaderboardScores>

      {loader}
    </Popup>
  );
};
